import * as React from 'react';

import type {
    CalendarMonthSurfaceItemProps,
    CalendarMonthSurfaceItemStyleProps,
    CalendarMonthSurfaceItemStyles,
} from './CalendarMonthSurfaceItem.types';
import { addDays, compare, endOfWeek, getDay, subDays } from 'owa-datetime';
import {
    getCalendarEventItemAriaLabel,
    getCalendarEventItemTitle,
} from 'owa-calendar-event-attributes';
import {
    getCalendarEventVisualDateRange,
    isItemMultiDayVisually,
} from 'owa-calendar-events-visual-date-range';
import { useBooleanState } from 'owa-react-hooks/lib/useBooleanState';

import type { CalendarEvent } from 'owa-calendar-types';
import TimeConstants from 'owa-datetime-utils/lib/TimeConstants';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { getUserOptions } from 'owa-session-store';
import { observer } from 'mobx-react-lite';

let monthSurfaceItemStyles = require('../CalendarMonthSurfaceItem.scss');
let classNames = require('classnames/bind').bind(monthSurfaceItemStyles);

const getClassNames = classNamesFunction<
    CalendarMonthSurfaceItemStyleProps,
    CalendarMonthSurfaceItemStyles
>();

/**
 * Template for calendar items on month view
 */
export default observer(function CalendarMonthSurfaceItemBase(
    props: CalendarMonthSurfaceItemProps
) {
    const [hover, setHover, unsetHover] = useBooleanState(false);
    let {
        item,
        theme,
        className,
        styles,
        itemState,
        onRender,
        userOptions,
        dateRange,
        disableTabIndex,
        colorScheme,
        charmId,
        isAgendaItem,
        isSelectedDay,
        increaseFontSize,
        shouldHideTooltip,
    } = props;
    const getTooltip = (item: CalendarEvent) => {
        if (hover && shouldHideTooltip?.()) {
            return null;
        }
        return getCalendarEventItemTitle(item);
    };
    const useBoldTheme = getUserOptions().UseBoldCalendarColorThemeReact;
    const classes = getClassNames(styles, {
        theme: theme!,
        className: className,
        freeBusyType: item.FreeBusyType,
        colorScheme: colorScheme,
        charmId: charmId,
        responseType: item.ResponseType,
        isAgendaItem: isAgendaItem,
        useBoldTheme: useBoldTheme,
        isSelectedDay: isSelectedDay,
    });
    let isMultiDayItem = isItemMultiDayVisually(item);
    let startOfMonthView =
        userOptions.weekStartDay > 0 && getDay(dateRange.start) < userOptions.weekStartDay
            ? subDays(
                  dateRange.start,
                  getDay(dateRange.start) + TimeConstants.DaysInOneWeek - userOptions.weekStartDay
              )
            : subDays(dateRange.start, getDay(dateRange.start) - userOptions.weekStartDay);
    let weekNum = itemState ? itemState.week : 1;
    let weekStart = addDays(startOfMonthView, TimeConstants.DaysInOneWeek * weekNum);
    let weekEnd = endOfWeek(weekStart, userOptions.weekStartDay);
    let hasStartPoint = compare(item.Start, weekStart) >= 0;
    let hasEndPoint = compare(weekEnd, getCalendarEventVisualDateRange(item).end) >= 0;
    let childrenProps = {
        contentClass: classes.content,
        padding: classes.paddingForContainer,
        item: item,
        itemState: itemState,
        isMultiDayItem: isMultiDayItem,
    };
    let itemClassNames = classNames(
        classes.root,
        monthSurfaceItemStyles.contentWrapper,
        {
            itemIsCancelled: item.IsCancelled,
            isMultiDayItem: !useBoldTheme && isMultiDayItem,
            hasStartPoint: !useBoldTheme && isMultiDayItem && hasStartPoint,
            hasEndPoint: !useBoldTheme && (!isAgendaItem ? isMultiDayItem && hasEndPoint : false),
            [monthSurfaceItemStyles.increaseFontSizes]: increaseFontSize,
        },
        childrenProps.contentClass,
        isAgendaItem ? classes.agendaContent : null,
        isSelectedDay && monthSurfaceItemStyles.isSelectedDay
    );
    return (
        <div
            role="button"
            className={itemClassNames}
            tabIndex={disableTabIndex ? undefined : 0}
            title={getTooltip(item)}
            onMouseOver={setHover}
            onMouseLeave={unsetHover}
            aria-label={getCalendarEventItemAriaLabel(item, null /* timezoneId */, isSelectedDay)}>
            {onRender?.(childrenProps)}
        </div>
    );
});
